// --- Core ---
import {CSSProperties, ReactElement} from "react";
import {FormattedMessage} from "react-intl";
// --- MUI ---
import {DialogTitle as MUIDialogTitle, IconButton, Stack, Tooltip, Typography} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
// --- SaniQ ---
import {DeserializedRecord} from "../models/Helpers";
import {usePersistedState} from "hooks/usePersistedState";
import {FormatDateTime} from "shared/DateTime";
import {useAppSelector} from "store";
import {ShortcutLabel} from "../modules/navigation/components/ShortcutLabel";

interface DialogTitleProps {
  title: ReactElement | string;
  subtitle?: ReactElement | string;
  adornment?: ReactElement;
  onClose: () => void;
  onDelete?: (id: string, paranoid?: boolean) => void;
  record?: DeserializedRecord | Partial<DeserializedRecord>;
  titleStyles?: CSSProperties
}

const DialogTitle = ({adornment = null, title, titleStyles, onClose, record, subtitle}: DialogTitleProps) => {
  const isHotkeyOverlayDisplayed = useAppSelector(state => state.hotkeys.isOverlayDisplayed);

  return (
    <MUIDialogTitle style={titleStyles}>
      <Stack
        direction="row"
        spacing={2}
        sx={{justifyContent: "space-between", alignItems: "center"}}
      >
        <Stack>
          <Typography variant={"h5"}>{title}</Typography>
          {subtitle && <Typography variant={"subtitle1"}>{subtitle}</Typography>}
          {
            record?.id &&
            <Tooltip
              arrow
              title={
                <FormattedMessage
                  id="dialog_form.subtitle.createdAt"
                  defaultMessage="Created: {date}"
                  values={{date: <FormatDateTime value={record.createdAt}/>}}
                />
              }
            >
              <Typography variant="caption">
                <FormattedMessage
                  id="dialog_form.subtitle.updatedAt"
                  defaultMessage="ID: {id}, Last Update: {date}"
                  values={{date: <FormatDateTime value={record.updatedAt}/>, id: record.id}}
                />
              </Typography>
            </Tooltip>
          }
        </Stack>
        <Stack direction="row" spacing={1} sx={{alignItems: "center"}}>
          {adornment}
          <Tooltip
            title={
              <ShortcutLabel
                keys={["ctrl", "x"]}
              />
            }
            open={isHotkeyOverlayDisplayed}
          >
            <IconButton autoFocus onClick={onClose}>
              <CloseIcon/>
            </IconButton>
          </Tooltip>
        </Stack>
      </Stack>
    </MUIDialogTitle>
  );
};

type handleClose = () => void;
type handleOpen = () => void;
type handleToggle = () => void;
type DialogToggle = [boolean, handleOpen, handleClose, handleToggle];

const useDialogToggle = (initialValue: boolean = false, lsKey: string = undefined): DialogToggle => {
  const [state, setState] = usePersistedState<boolean>(initialValue, lsKey);
  const handleOpen = () => setState(true);
  const handleClose = () => setState(false);
  const handleToggle = () => setState(state => !state);

  return [state, handleOpen, handleClose, handleToggle];
};

export {
  DialogTitle,
  DialogTitleProps,
  useDialogToggle
};
